import { ApartmentOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import type { MenuProps } from "antd";
import { App, Breadcrumb, Button, Card, Dropdown, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import type { DepartmentDetail } from "../types";
import { collapseAncestors } from "../utils/breadcrumb";

const { Title, Text } = Typography;

interface DepartmentProfileHeaderProps {
	department: DepartmentDetail;
	onEdit: () => void;
	onAddChild: () => void;
	onMove: () => void;
	/**
	 * ⚠️ Faqat foydalanuvchi TASDIQLAGANDAN keyin chaqiriladi — tasdiq
	 * oynasini sarlavhaning O'ZI ko'rsatadi. Chaqiruvchi to'g'ridan-to'g'ri
	 * mutatsiya uzatishi mumkin (`() => archiveDepartment.mutate(id)`).
	 */
	onArchive: () => void;
	/** `onArchive` bilan bir xil kontrakt — tasdiqdan KEYIN chaqiriladi. */
	onUnarchive: () => void;
}

/**
 * Bir fakt ustuni — yorliq ustida, qiymat ostida (`EmployeeProfileHeader`
 * dagi `Fact` bilan AYNI shakl; modul chegarasi uchun alohida nusxa).
 *
 * ⚠️ Bo'sh qiymat `—` bilan chiziladi, ustunning O'ZI olib tashlanmaydi.
 */
function Fact({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="min-w-0">
			<div className="text-xs text-[#8a929e] mb-1">{label}</div>
			<div className="text-sm text-[#14171c] truncate">{children}</div>
		</div>
	);
}

/**
 * `Bo'limlar / …ajdodlar… / joriy bo'lim`.
 *
 * ⚠️ `shared/components/Breadcrumbs` ISHLATILMAYDI: u faqat `to: string`
 * oladi (tipli `params` yo'q) va yig'ilgan "…" bo'g'ini uchun tooltip bera
 * olmaydi. Ko'rinish esa u bilan BIR XIL (12px, `#8a929e` ajratgich,
 * `#1c5cab` havola, `#14171c` + `font-medium` joriy bo'g'in).
 *
 * Yig'ish qoidasi to'liq `collapseAncestors` da — bu yerda natija
 * FILTRLANMAYDI.
 */
function DepartmentBreadcrumb({ department }: { department: DepartmentDetail }) {
	const { t } = useTranslation("departments");

	const crumbs = collapseAncestors(department.ancestors);

	return (
		<Breadcrumb
			className="text-xs"
			separator={<span className="text-[#8a929e]">/</span>}
			items={[
				{
					key: "root",
					title: (
						<Link to="/departments" className="text-[#1c5cab] hover:underline">
							{t("breadcrumb.root")}
						</Link>
					),
				},
				...crumbs.map((c, i) => ({
					key: c.id ?? `collapsed-${i}`,
					title: c.collapsed ? (
						<Tooltip title={c.collapsed.map((a) => a.name).join(" / ")}>
							<span className="text-[#8a929e] border border-dashed rounded px-1">{c.label}</span>
						</Tooltip>
					) : c.id ? (
						<Link
							to="/departments/$id"
							params={{ id: c.id }}
							className="text-[#1c5cab] hover:underline"
						>
							{c.label}
						</Link>
					) : (
						c.label
					),
				})),
				{
					key: "current",
					title: <span className="text-[#14171c] font-medium">{department.name}</span>,
				},
			]}
		/>
	);
}

/**
 * Arxivlash tasdig'i — Sozlamalar yorlig'idagi `Popconfirm` bilan BIR XIL
 * matnlar (`settingsTab.archiveConfirm*`).
 *
 * ⚠️ `Popconfirm` EMAS, `modal.confirm`: menyu bandini bosganda `Dropdown`
 * yopiladi va uning ichidagi `Popconfirm` ham birga yo'qolardi.
 * `App.useApp()` — statik `Modal.confirm` loyiha `ConfigProvider` i
 * (tema, lokal) ni ko'rmaydi.
 */
function useArchiveConfirm(onArchive: () => void): () => void {
	const { t } = useTranslation("departments");
	const { modal } = App.useApp();

	return () => {
		modal.confirm({
			title: t("settingsTab.archiveConfirmTitle"),
			content: t("settingsTab.archiveConfirmDescription"),
			okText: t("detail.archiveButton"),
			okButtonProps: { danger: true },
			onOk: onArchive,
		});
	};
}

function useUnarchiveConfirm(onUnarchive: () => void): () => void {
	const { t } = useTranslation("departments");
	const { modal } = App.useApp();

	return () => {
		modal.confirm({
			title: t("settingsTab.unarchiveConfirmTitle"),
			content: t("settingsTab.unarchiveConfirmDescription"),
			okText: t("detail.unarchiveButton"),
			onOk: onUnarchive,
		});
	};
}

/**
 * Profil kartasining amallari — «Tahrirlash», «Yangi bo'linma» va "…"
 * menyusi («Ko'chirish», «Arxivlash»).
 *
 * ⚠️⚠️ Ruxsat bo'lmasa tugma UMUMAN chizilmaydi, `disabled` QILINMAYDI
 * (`EmployeeHeaderActions` naqshi): `departments:update` / `:create` /
 * `:update` / `:archive`. "…" menyusi ham faqat kamida bitta ruxsat etilgan
 * amal bo'lganda chiziladi — bo'sh menyu noto'g'ri va'da. Arxivlangan
 * bo'limda «Arxivlash» bandi chiqmaydi.
 *
 * ⚠️⚠️ «Arxivlash» bandi `onArchive` ni TO'G'RIDAN-TO'G'RI chaqirmaydi —
 * avval tasdiq oynasi (`useArchiveConfirm`). Sozlamalar yorlig'idagi tugma
 * `Popconfirm` ortida; sarlavha ikkinchi yo'l bo'lib, tasdiqni chetlab
 * o'tmasligi shart.
 */
function DepartmentHeaderActions({
	department,
	onEdit,
	onAddChild,
	onMove,
	onArchive,
	onUnarchive,
}: DepartmentProfileHeaderProps) {
	const { t } = useTranslation("departments");
	const { can } = usePermission();
	const confirmArchive = useArchiveConfirm(onArchive);
	const confirmUnarchive = useUnarchiveConfirm(onUnarchive);

	const tahrirlash = can("departments", "update");
	// ⚠️ Arxivlangan bo'limga farzand qo'shib bo'lmaydi (backend 409
	// `departments.parentArchived`) — tugma UMUMAN chizilmaydi (review
	// Important 2 UI).
	const qoshish = can("departments", "create") && department.isActive;
	const kochirish = can("departments", "update");
	const arxivlash = can("departments", "archive") && department.isActive;
	const arxivdanChiqarish = can("departments", "archive") && !department.isActive;

	const menuItems: NonNullable<MenuProps["items"]> = [
		...(kochirish ? [{ key: "move", label: t("detail.moveButton"), onClick: onMove }] : []),
		...(arxivlash
			? [
					{
						key: "archive",
						label: t("detail.archiveButton"),
						danger: true,
						onClick: confirmArchive,
					},
				]
			: []),
		...(arxivdanChiqarish
			? [{ key: "unarchive", label: t("detail.unarchiveButton"), onClick: confirmUnarchive }]
			: []),
	];

	if (!(tahrirlash || qoshish || menuItems.length > 0)) {
		return null;
	}

	return (
		<Space size="small">
			{tahrirlash ? (
				<Button icon={<EditOutlined />} onClick={onEdit}>
					{t("detail.editButton")}
				</Button>
			) : null}
			{qoshish ? (
				<Button icon={<PlusOutlined />} onClick={onAddChild}>
					{t("detail.addChildButton")}
				</Button>
			) : null}
			{menuItems.length > 0 ? (
				<Dropdown trigger={["click"]} menu={{ items: menuItems }}>
					<Button icon={<MoreOutlined />} aria-label={t("detail.moreActions")} />
				</Dropdown>
			) : null}
		</Space>
	);
}

/** Bo'lim profilining yuqori qismi — breadcrumb + profil kartasi. */
export function DepartmentProfileHeader(props: DepartmentProfileHeaderProps) {
	const { department } = props;
	const { t } = useTranslation("departments");

	const parent = department.ancestors[department.ancestors.length - 1];

	return (
		<div className="flex flex-col gap-3">
			<DepartmentBreadcrumb department={department} />

			<Card>
				<div className="flex items-start gap-4">
					<div
						aria-hidden
						className="w-16 h-16 rounded-2xl bg-[#eaf1fb] text-[#1c5cab] text-2xl flex items-center justify-center flex-shrink-0"
					>
						<ApartmentOutlined />
					</div>

					<div className="min-w-0 flex-1">
						<div className="flex items-start gap-4">
							<Space size="small" wrap align="center" className="min-w-0 flex-1">
								<Title level={4} className="!mb-0">
									{department.name}
								</Title>
								<Tag color="purple">{`${department.gradeLevel} · ${department.gradeName}`}</Tag>
								<Tag color={department.isActive ? "green" : "default"}>
									{department.isActive ? t("detail.statusActive") : t("detail.statusArchived")}
								</Tag>
							</Space>

							<DepartmentHeaderActions {...props} />
						</div>

						{department.code ? (
							<div className="mt-1">
								<Text type="secondary" className="text-xs">
									{`${t("detail.codeLabel")}: ${department.code}`}
								</Text>
							</div>
						) : null}

						<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#eeefec]">
							<Fact label={t("detail.parentLabel")}>
								{department.parentId && parent ? (
									<Link
										to="/departments/$id"
										params={{ id: department.parentId }}
										className="text-[#1c5cab] hover:underline"
									>
										{parent.name}
									</Link>
								) : (
									"—"
								)}
							</Fact>
							<Fact label={t("detail.createdAtLabel")}>
								{dayjs(department.createdAt).format("DD.MM.YYYY")}
							</Fact>
						</div>
					</div>
				</div>
			</Card>
		</div>
	);
}
